import {GET_ASSET_CLASSES} from 'graphql/queries/assetManagement';
import {toast} from 'react-toastify';
import {supportedLanguages} from 'utils/constants';

// Fund records can carry a null language, which puts a literal "null" in the
// edit route. Passing that straight through fails the LanguageEnum, so
// normalise before it reaches a mutation. EN matches the gateway's own
// resolution order (requested -> JWT -> EN).
const safeLanguage = value =>
  supportedLanguages.some(l => l.value === value) ? value : 'EN';

export const createAssetClassResolver = (
  createAssetClass,
  formValues,
  navigate,
  closeModal,
) => {
  const {name, description, language, status, key, instruments} = formValues;
  createAssetClass({
    variables: {
      input: {
        name,
        language,
        description,
        status,
        key,
        instruments: instruments.map(instrument => ({
          name: instrument?.name,
          description: instrument?.description,
          status: instrument?.status,
          key: instrument?.key,
        })),
      },
    },
    refetchQueries: [{query: GET_ASSET_CLASSES}],
  })
    .then(({data: {createAssetClass}}) => {
      if (createAssetClass.__typename === 'Error') {
        throw new Error(createAssetClass.message);
      } else {
        toast.success('Asset Class Created Successfully');
        closeModal(false);
        navigate(-1);
      }
    })
    .catch(error => {
      toast.error(`GraphQL Error: ${error.message}`);
    });
};

export const handleDeleteAssetClass = (
  deleteAssetClass,
  id,
  refetch,
  closeModal,
) => {
  deleteAssetClass({
    variables: {
      deleteAssetClassId: id,
    },
  })
    .then(({data: {deleteAssetClass}}) => {
      if (deleteAssetClass.__typename === 'Error') {
        throw new Error(deleteAssetClass.message);
      } else {
        toast.success('Asset Class Deleted Successfully');
        refetch();
        closeModal(false);
      }
    })
    .catch(error => {
      toast.error(`GraphQL Error: ${error.message}`);
    });
};

export const handleUpdateAssetClass = (
  updateAssetClass,
  formValues,
  id,
  navigate,
  closeModal,
) => {
  const {name, description, language, status, key, instruments} = formValues;
  updateAssetClass({
    variables: {
      updateAssetClassId: id,
      input: {
        name,
        language,
        description,
        status,
        key,
        instruments: instruments.map(instrument => ({
          name: instrument?.name,
          description: instrument?.description,
          status: instrument?.status,
          key: instrument?.key,
        })),
      },
    },
    refetchQueries: [{query: GET_ASSET_CLASSES}],
  })
    .then(({data: {updateAssetClass}}) => {
      if (updateAssetClass.__typename === 'Error') {
        throw new Error(updateAssetClass.message);
      } else {
        toast.success('Asset Class Updated Successfully');
        closeModal(false);
        navigate(`/dashboard/asset-management/classes`);
      }
    })
    .catch(error => {
      toast.error(`GraphQL Error: ${error.message}`);
    });
};

export const handleUpdateBond = (
  updateBond,
  id,
  language,
  updatedValues,
  refetch,
  setOpenUpdateModal,
  stepTwo,
) => {
  updateBond({
    variables: {
      updateBondId: id,
      language: safeLanguage(language),
      input: {
        ...(stepTwo
          ? {
              ...updatedValues,
              callable: updatedValues.callable ?? false,
            }
          : {
              baseFinancialInstrumentInput: {
                ...updatedValues,
              },
            }),
      },
    },
  })
    .then(({data: {updateBond}}) => {
      if (updateBond.__typename === 'Error') {
        toast.error(updateBond.message);
      } else {
        toast.success('Updated Successfully');
        refetch();
        setOpenUpdateModal(false);
      }
    })
    // `toast.error(message, error)` puts the reason in the options slot,
    // where it is discarded — so a rejected update said only "Something
    // went wrong" even when the gateway named the offending field.
    .catch(error => toast.error(error?.message || 'Something went wrong'));
};

export const handleUpdateMutualFund = (
  updateMutualFund,
  id,
  language,
  updatedValues,
  refetch,
  setOpenUpdateModal,
  stepTwo,
) => {
  const input = {};
  if (stepTwo) {
    for (const key in updatedValues) {
      input[key] = updatedValues[key];
    }
  } else {
    input.baseFinancialInstrumentInput = {};
    for (const key in updatedValues) {
      input.baseFinancialInstrumentInput[key] = updatedValues[key];
    }
  }

  updateMutualFund({
    variables: {
      updateMutualFundId: id,
      language: safeLanguage(language),
      input,
    },
  })
    .then(({data: {updateMutualFund}}) => {
      if (updateMutualFund.__typename === 'Error') {
        toast.error(updateMutualFund.message);
      } else {
        toast.success('Updated Successfully');
        refetch();
        setOpenUpdateModal(false);
      }
    })
    // `toast.error(message, error)` puts the reason in the options slot,
    // where it is discarded — so a rejected update said only "Something
    // went wrong" even when the gateway named the offending field.
    .catch(error => toast.error(error?.message || 'Something went wrong'));
};

export const createInstrumentGroup = (
  resolver,
  values,
  banner,
  multilang,
  assetClass,
  key,
  navigate,
  id,
) => {
  resolver({
    variables: {
      input: {
        title: values.title,
        description: values?.description ?? null,
        instruments: values?.instruments,
        banner,
        id,
        ...(values?.riskLevel && {riskLevel: values?.riskLevel}),
        language: values.language,
        multilang,
        assetClassKey: assetClass,
        instrumentKey: key,
      },
    },
  })
    .then(({data: {groupFinancialInstruments}}) => {
      if (groupFinancialInstruments.__typename === 'Error') {
        toast.error(groupFinancialInstruments.message);
      } else {
        toast.success('Instrument Group Created Successfully');
        navigate(-1);
      }
    })
    .catch(error => toast.error(`GraphQL Error: ${error.message}`));
};

/**
 * Step-one values go under `baseFinancialInstrumentInput`; step two sends the
 * concrete fields flat, exactly as the bond and fund handlers do.
 */
const submitInstrument = (
  resolver,
  variables,
  responseKey,
  label,
  refetch,
  setOpenUpdateModal,
) =>
  resolver({variables})
    .then(({data}) => {
      const res = data?.[responseKey];
      if (res?.__typename === 'Error') {
        toast.error(res?.message);
      } else {
        toast.success(`${label} updated successfully`);
        refetch?.();
        setOpenUpdateModal?.(false);
      }
    })
    .catch(error => toast.error(`GraphQL Error: ${error.message}`));

export const handleUpdateIpo = (
  updateIpo,
  id,
  language,
  updatedValues,
  refetch,
  setOpenUpdateModal,
  stepTwo,
) =>
  submitInstrument(
    updateIpo,
    {
      updateIpoId: id,
      language: safeLanguage(language),
      input: stepTwo
        ? {...updatedValues}
        : {baseFinancialInstrumentInput: {...updatedValues}},
    },
    'updateIpo',
    'IPO',
    refetch,
    setOpenUpdateModal,
  );

export const handleUpdateEquity = (
  updatePublicEquity,
  id,
  language,
  updatedValues,
  refetch,
  setOpenUpdateModal,
  stepTwo,
) =>
  submitInstrument(
    updatePublicEquity,
    {
      updatePublicEquityId: id,
      input: stepTwo
        ? {...updatedValues}
        : {baseFinancialInstrumentInput: {...updatedValues}},
    },
    'updatePublicEquity',
    'Equity',
    refetch,
    setOpenUpdateModal,
  );
